import React, {useState} from 'react';
import {Message} from "types/Message";
import {useNotifications} from "context/NotificationContext";
import ConfirmModal from "components/ConfirmModal";
import IconName from "types/IconName";
import {useMutation, useQuery} from "react-query";
import ngage from "services/ngage";
import {invalidate} from "services/query-client";
import {MultiCombobox} from "components/Form/Form/Control";

// Helpers ----

const userOptions = (roles: Array<string>) => ngage.users
    .filtered({page: 1, perPage: 100, roles: roles.join(',')})
    .then(page => page?.data || [])
    .then(users => users.map(user => ({
        id: user.id,
        name: user.name,
        description: user.role_names.join(', '),
        icon: IconName.USER
    })))

// Component ----

type TestMessageProps = {
    message: Message | null
    show: boolean
    onSend?: (message: Message, userIds: Array<number | string>) => any
    onSendFail?: (message: Message, userIds: Array<number | string>, error: unknown) => any
    onCancel?: () => any
}

function TestMessage({message, onCancel, onSend, onSendFail, show}: TestMessageProps) {
    const [selectedUserIds, setSelectedUserIds] = useState<Array<number | string>>([]);

    const {data: users} = useQuery(['users', 'operator'], () => userOptions([
        'administrator', 'staff', 'volunteer'
    ]))

    const {mutateAsync: sendMessage} = useMutation(ngage.messages.sendTest)

    const {notify} = useNotifications()

    const handleCancel = () => {
        setSelectedUserIds([])
        if (onCancel) onCancel()
    }

    const handleSendMessage = async () => {
        if (!message || selectedUserIds.length < 1) return

        try {
            await sendMessage({messageId: message.id, recipientIds: selectedUserIds})
            await invalidate('messages')

            notify("success", "Message Test Sent", `${message.subject} has been sent to the selected recipients`)

            if (onSend) onSend(message, selectedUserIds)

            setSelectedUserIds([])
        } catch (error: any) {
            notify("error", "Message Test Not Sent", `${message.subject} could not be sent to the selected recipients`)

            if (onSendFail) onSendFail(message, selectedUserIds, error)
        }
    }

    return (
        <ConfirmModal
            show={show}
            title="Send a Test Message"
            body={`Select which operator users will receive a temporary test copy of ${message? `'${message.subject}'` : 'the selected message'}. Any attached vouchers will not be valid.`}
            approveLabel="Send"
            onApprove={handleSendMessage}
            onCancel={handleCancel}
        >
            <div className="w-full">
                <MultiCombobox
                    options={users || []}
                    selected={users?.filter(user => selectedUserIds.includes(user.id.toString()))}
                    onChange={users => setSelectedUserIds(users.map(user => String(user.id)))}
                />
            </div>
        </ConfirmModal>
    );
}

export default TestMessage;